/**
 * Exporter — Exports the Fabric.js canvas to PNG files at specific dimensions.
 */
import { canvasToBlob, downloadCanvas } from '../utils/imageUtils.js';

export class Exporter {
  /**
   * @param {import('../canvas/CanvasManager.js').CanvasManager} canvasManager
   */
  constructor(canvasManager) {
    this.canvasManager = canvasManager;
  }

  /**
   * Export the canvas as a PNG at the current template dimensions.
   * @param {string} [filename] - Download filename
   * @returns {Promise<void>}
   */
  async exportPNG(filename) {
    const { width, height } = this.canvasManager;
    const defaultFilename = filename || `banner-${width}x${height}.png`;
    const canvasEl = this.canvasManager.toCanvasElement(width, height);
    await downloadCanvas(canvasEl, defaultFilename);
  }

  /**
   * Export at a specific size, regardless of current canvas dimensions.
   * @param {number} targetWidth
   * @param {number} targetHeight
   * @param {string} [filename]
   * @returns {Promise<void>}
   */
  async exportAtSize(targetWidth, targetHeight, filename) {
    const defaultFilename = filename || `banner-${targetWidth}x${targetHeight}.png`;
    const canvasEl = this.canvasManager.toCanvasElement(targetWidth, targetHeight);
    await downloadCanvas(canvasEl, defaultFilename);
  }

  /**
   * Export 1600x300 wide banner.
   * @param {string} [filename]
   */
  async exportWide(filename) {
    await this.exportAtSize(1600, 300, filename || 'banner-1600x300.png');
  }

  /**
   * Export 1500x750 rectangular banner.
   * @param {string} [filename]
   */
  async exportRectangle(filename) {
    await this.exportAtSize(1500, 750, filename || 'banner-1500x750.png');
  }

  /**
   * Get a preview data URL at reduced size.
   * @param {number} [maxWidth=800]
   * @returns {string}
   */
  getPreviewDataURL(maxWidth = 800) {
    const multiplier = maxWidth / this.canvasManager.width;
    return this.canvasManager.toDataURL({
      format: 'png',
      multiplier: Math.min(multiplier, 1),
    });
  }
}
